import { useEffect, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'offline';

export interface AutoSaveIndicatorProps {
  status: AutoSaveStatus;
  /** Epoch ms of the last successful save, for the "Saved Xs ago" label. */
  lastSavedAt: number | null;
  /** Optional reason shown on error (e.g. "conflict"). */
  reason?: string | null;
  onRetry?: () => void;
}

/** Compact auto-save status pill for the editor title bar. */
export function AutoSaveIndicator({ status, lastSavedAt, reason, onRetry }: AutoSaveIndicatorProps) {
  // Re-render every few seconds so the "Saved Xs ago" stays fresh.
  const [, force] = useState(0);
  useEffect(() => {
    if (status !== 'saved' || lastSavedAt == null) return;
    const t = setInterval(() => force((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, [status, lastSavedAt]);

  let text: string;
  if (status === 'dirty') text = 'Unsaved changes';
  else if (status === 'saving') text = 'Saving…';
  else if (status === 'offline') text = 'Idle save paused';
  else if (status === 'error') {
    if (reason === 'conflict') text = 'Newer version exists';
    else if (reason === 'busy') text = 'Save busy';
    else text = 'Save failed';
  }
  else if (status === 'saved' && lastSavedAt != null) text = `Saved ${relativeTime(lastSavedAt)}`;
  else return null;

  return (
    <span className={`editor-autosave editor-autosave--${status}`} aria-live="polite">
      <span className="editor-autosave__dot" aria-hidden="true" />
      <span className="editor-autosave__text">{text}</span>
      {status === 'error' && onRetry ? (
        <button type="button" className="editor-autosave__retry" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </span>
  );
}

function relativeTime(epochMs: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - epochMs) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
}
